import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { VictoryLabel } from 'victory';
import { useTheme } from '@material-ui/core/styles';

const AVG_CHAR_WIDTH_FACTOR = 0.6;
const FULL_PATH_FONT_SIZE = 13;

const truncateText = (text, maxWidth, fontSize) => {
  const maxChars = Math.floor(maxWidth / (fontSize * AVG_CHAR_WIDTH_FACTOR));

  return text.length <= maxChars
    ? text
    : `${text.substring(0, maxChars - 3)}...`;
};

const getTruncatedText = ({ fontSize, hasFullPath, maxWidth, text }) => {
  if (!hasFullPath) return truncateText(text[0], maxWidth, fontSize);

  const [fullPath, displayText] = text;

  return [
    truncateText(fullPath, maxWidth, FULL_PATH_FONT_SIZE),
    truncateText(displayText, maxWidth, fontSize)
  ];
};

const getLabelStyles = ({ hasFullPath, theme, tickLabelStyles, isHovered }) => {
  const labelStyles = hasFullPath
    ? [
        {
          ...tickLabelStyles,
          fontSize: FULL_PATH_FONT_SIZE,
          fill: theme.palette.text.secondary
        },
        tickLabelStyles
      ]
    : tickLabelStyles;

  const backgroundStyles = {
    fill: isHovered ? theme.palette.grey[100] : 'transparent'
  };

  return { labelStyles, backgroundStyles };
};

export const CustomVictoryYAxisLabel = ({
  onLabelClick,
  maxWidth,
  style,
  text,
  ...props
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const hasFullPath = text.length > 1;

  const labelText = getTruncatedText({
    fontSize: style.fontSize,
    hasFullPath,
    maxWidth,
    text
  });
  const { labelStyles, backgroundStyles } = getLabelStyles({
    hasFullPath,
    theme,
    tickLabelStyles: style,
    isHovered
  });

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const events = useMemo(
    () => ({
      onClick: onLabelClick,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    }),
    [onLabelClick, handleMouseEnter, handleMouseLeave]
  );

  return (
    <VictoryLabel
      {...props}
      dy={-5}
      backgroundPadding={style.backgroundPadding}
      backgroundStyle={backgroundStyles}
      text={labelText}
      style={labelStyles}
      events={events}
    />
  );
};

CustomVictoryYAxisLabel.propTypes = {
  onLabelClick: PropTypes.func.isRequired,
  maxWidth: PropTypes.number.isRequired,
  style: PropTypes.object,
  text: PropTypes.arrayOf(PropTypes.string)
};

export default CustomVictoryYAxisLabel;
